import type { GeoJsonProperties } from 'geojson';

import { formatIsoDate } from '@/utils/dateUtils';

/**
 * Air-valve feature properties as emitted by GET /airvalves/{bbox}.
 * Mirrors the backend GetAirValvesEndpoint property dictionary:
 *   name (asset name), dma, highRisk (boolean), w3w (what3words), sc (sub-client).
 *
 * The viewport feed flags `highRisk` but does NOT carry the high-risk detail fields
 * (date identified, reasons) — those live on the /airvalves/hr feed and are matched
 * onto the hovered valve by asset `name` (see AirValvePopup).
 */
export interface AirValveFeatureProperties {
    name: string;
    dma: string;
    highRisk: boolean;
    w3w: string | null;
    sc: string | null;
}

/**
 * High-risk air-valve feature properties as emitted by GET /airvalves/hr.
 * Mirrors the backend GetHighRiskAirValvesEndpoint property dictionary:
 *   name, dma, identified (ISO date), reasons, w3w, sc.
 */
export interface HighRiskAirValveFeatureProperties {
    name: string;
    dma: string;
    /** ISO "yyyy-MM-dd" date the valve was identified as high-risk, or null. */
    identified: string | null;
    reasons: string | null;
    w3w: string | null;
    sc: string | null;
}

/** Reads a string property, returning null for missing/empty/non-string values. */
function readString(props: GeoJsonProperties, key: string): string | null {
    if (!props) return null;
    const value: unknown = props[key];
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

/** Normalises a raw GeoJSON feature's properties into AirValveFeatureProperties. */
export function readAirValveProperties(props: GeoJsonProperties): AirValveFeatureProperties {
    return {
        name: readString(props, 'name') ?? '',
        dma: readString(props, 'dma') ?? '',
        highRisk: props?.['highRisk'] === true,
        w3w: readString(props, 'w3w'),
        sc: readString(props, 'sc'),
    };
}

/** Normalises a raw GeoJSON feature's properties into HighRiskAirValveFeatureProperties. */
export function readHighRiskAirValveProperties(props: GeoJsonProperties): HighRiskAirValveFeatureProperties {
    return {
        name: readString(props, 'name') ?? '',
        dma: readString(props, 'dma') ?? '',
        identified: readString(props, 'identified'),
        reasons: readString(props, 'reasons'),
        w3w: readString(props, 'w3w'),
        sc: readString(props, 'sc'),
    };
}

/** Placeholder shown for a missing "Date Identified" value (per the SAVI designs). */
export const AIR_VALVE_DATE_PLACEHOLDER = 'DD/MM/YY';

/** Placeholder shown for any missing air-valve text field. */
export const AIR_VALVE_FIELD_PLACEHOLDER = '—';

/** Formats an ISO "yyyy-MM-dd" identified date to "DD/MM/YYYY", or null when absent/malformed. */
export function formatAirValveDate(identified: string | null | undefined): string | null {
    return formatIsoDate(identified);
}
